/**
 * priceNotification — remembers which markets have already shown the
 * "Service prices now include all fees" notice.
 *
 * The notice is an all-fees-included price explainer, so it only needs to land
 * once per market: a returning client who has already read it for Southern
 * California shouldn't get it again on every trip through booking step 2, but
 * should still see it the first time they price a different market whose fees
 * roll up differently.
 *
 * The seen list lives in localStorage (via storageMode, which falls back to
 * memory when storage is unavailable and is safe during SSR) under a single
 * key holding an array of market ids.
 */
import { storageMode } from './storage'

export const PRICE_NOTIFICATION_STORAGE_KEY = 'priceNotificationSeenMarkets'

/**
 * getSeenPriceNotificationMarkets: the market ids that have already shown the notice.
 *
 * Tolerates anything unparseable or non-array in storage (hand-edited, written
 * by an older build) by treating it as an empty list rather than throwing.
 *
 * @return {string[]} Market ids, newest last
 */
export const getSeenPriceNotificationMarkets = () => {
  try {
    const stored = JSON.parse(storageMode('localStorage').getItem(PRICE_NOTIFICATION_STORAGE_KEY))

    return Array.isArray(stored) ? stored.filter(Boolean).map(String) : []
  } catch {
    // Unparseable JSON — treat the device as having seen nothing.
    return []
  }
}

/**
 * hasSeenPriceNotification: has this market already shown the notice on this device?
 *
 * @param {string} marketId The market the client is currently pricing
 * @return {boolean} True when the notice has already been shown for that market
 */
export const hasSeenPriceNotification = (marketId) =>
  !!marketId && getSeenPriceNotificationMarkets().includes(String(marketId))

/**
 * markPriceNotificationSeen: record that the notice has been shown for a market.
 *
 * Idempotent — re-marking an already-seen market leaves the list untouched.
 *
 * @param {string} marketId The market the notice was shown for
 * @return {void}
 */
export const markPriceNotificationSeen = (marketId) => {
  if (!marketId || hasSeenPriceNotification(marketId)) {
    return
  }

  const seen = [...getSeenPriceNotificationMarkets(), String(marketId)]

  try {
    storageMode('localStorage').setItem(PRICE_NOTIFICATION_STORAGE_KEY, JSON.stringify(seen))
  } catch (error) {
    // A full or locked storage quota shouldn't break the booking flow — the
    // worst case is the notice showing again on the next visit.
    console.warn('markPriceNotificationSeen: unable to persist seen markets', error)
  }
}
